import { styled } from '@material-ui/core';

import { down } from '../../../assets/style/breakpointHelpers';
import { domTag } from '../styledFilter';

export const VerificationCard = styled('div')({
	'width': '100%',
	'border': '1px solid var(--color-border-subtle)',
	'borderRadius': '20px',
	'padding': '24px',
	'background': 'linear-gradient(180deg, rgba(30, 86, 49, 0.04) 0%, rgba(255, 255, 255, 0) 80%)',
	'backgroundColor': 'var(--color-bg-elevated)',
	'boxShadow': 'var(--shadow-md)',
	'display': 'flex',
	'flexDirection': 'column',
	'gap': '20px',
	'position': 'relative',
	'overflow': 'hidden',

	'&:before': {
		content: '""',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		height: '4px',
		background: 'linear-gradient(90deg, var(--color-brand-primary) 0%, var(--color-brand-accent) 100%)',
	},

	[down('md')]: {
		padding: '20px',
		borderRadius: '16px',
	},
});

export const VerificationHeader = styled('div')({
	'display': 'flex',
	'alignItems': 'center',
	'justifyContent': 'space-between',
	'gap': '12px',

	'& .titleblock': {
		display: 'flex',
		flexDirection: 'column',
		gap: '4px',
	},

	'& .eyebrow': {
		fontFamily: 'var(--font-family-medium)',
		fontSize: '10px',
		letterSpacing: 'var(--tracking-widest)',
		textTransform: 'uppercase',
		color: 'var(--color-brand-primary)',
		margin: 0,
	},

	'& .title': {
		fontFamily: 'var(--font-family-display)',
		fontSize: '20px',
		lineHeight: 1.2,
		color: 'var(--color-text-primary)',
		margin: 0,
	},

	'& .progressbadge': {
		fontFamily: 'var(--font-family-semibold)',
		fontSize: '11px',
		letterSpacing: 'var(--tracking-wider)',
		textTransform: 'uppercase',
		color: 'var(--color-brand-primary)',
		backgroundColor: 'rgba(30, 86, 49, 0.08)',
		padding: '6px 10px',
		borderRadius: '999px',
		whiteSpace: 'nowrap',
	},
});

export const VerificationSlot = styled(domTag('div', ['status']))(
	({ status }: { status: 'idle' | 'converting' | 'busy' | 'success' | 'warning' | 'failed' }) => ({
		'display': 'grid',
		'gridTemplateColumns': '72px 1fr',
		'gridTemplateAreas': '"thumb info" "action action"',
		'alignItems': 'flex-start',
		'gap': '12px 14px',
		'padding': '14px',
		'border': `1px solid ${
			status === 'success'
				? 'var(--color-brand-accent)'
				: status === 'failed'
					? 'var(--color-error)'
					: status === 'warning'
						? 'var(--color-warning)'
						: 'var(--color-border-default)'
		}`,
		'borderRadius': '14px',
		'backgroundColor':
			status === 'success'
				? 'rgba(68, 186, 181, 0.04)'
				: status === 'failed'
					? 'rgba(239, 68, 68, 0.04)'
					: status === 'warning'
						? 'rgba(245, 158, 11, 0.04)'
						: 'var(--color-bg-secondary)',
		'transition': 'border-color 200ms ease, background-color 200ms ease',

		'& .thumbWrap': {
			gridArea: 'thumb',
		},

		'& .info': {
			gridArea: 'info',
			display: 'flex',
			flexDirection: 'column',
			gap: '4px',
			minWidth: 0,
		},

		'& .actionWrap': {
			gridArea: 'action',
			justifySelf: 'stretch',
			marginTop: '4px',
		},

		'& .infoTitle': {
			fontFamily: 'var(--font-family-semibold)',
			fontSize: 'var(--text-body-size)',
			color: 'var(--color-text-primary)',
			margin: 0,
			lineHeight: 1.3,
		},

		'& .infoSubtitle': {
			fontFamily: 'var(--font-family-regular)',
			fontSize: 'var(--text-body-sm-size)',
			color: 'var(--color-text-secondary)',
			margin: 0,
			lineHeight: 1.45,
		},

		[down('sm')]: {
			gridTemplateColumns: '56px 1fr',
			gap: '10px 12px',
			padding: '12px',
		},
	})
);

export const SlotThumb = styled('div')({
	'width': '72px',
	'height': '72px',
	'borderRadius': '14px',
	'backgroundColor': 'var(--color-bg-tertiary)',
	'border': '1px solid var(--color-border-subtle)',
	'display': 'flex',
	'alignItems': 'center',
	'justifyContent': 'center',
	'overflow': 'hidden',
	'position': 'relative',
	'flexShrink': 0,

	'&.circle': {
		borderRadius: '50%',
	},

	'& img': {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},

	'& .placeholder': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '4px',
		color: 'var(--color-text-tertiary)',
		fontSize: '10px',
		textAlign: 'center',
		padding: '6px',
		fontFamily: 'var(--font-family-medium)',
		textTransform: 'uppercase',
		letterSpacing: 'var(--tracking-wide)',
	},

	'& .checkOverlay': {
		position: 'absolute',
		bottom: '4px',
		right: '4px',
		width: '24px',
		height: '24px',
		borderRadius: '50%',
		backgroundColor: 'var(--color-brand-accent)',
		color: '#fff',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: '14px',
		fontFamily: 'var(--font-family-bold)',
		boxShadow: 'var(--shadow-md)',
		animation: 'verifyCheckPop 250ms cubic-bezier(0.34, 1.56, 0.64, 1)',
	},

	[down('sm')]: {
		width: '56px',
		height: '56px',
		borderRadius: '12px',
	},
});

export const SlotSpinner = styled('div')({
	width: '28px',
	height: '28px',
	border: '3px solid var(--color-border-default)',
	borderTopColor: 'var(--color-brand-primary)',
	borderRadius: '50%',
	animation: 'verifySpin 800ms linear infinite',
});

export const SlotActionButton = styled(domTag('button', ['variant']))(
	({ variant }: { variant: 'primary' | 'ghost' | 'danger' }) => ({
		'all': 'unset',
		'cursor': 'pointer',
		'display': 'inline-flex',
		'alignItems': 'center',
		'justifyContent': 'center',
		'gap': '6px',
		'padding': '10px 18px',
		'borderRadius': '10px',
		'fontFamily': 'var(--font-family-semibold)',
		'fontSize': '13px',
		'transition': 'all 180ms ease',
		'whiteSpace': 'nowrap',

		...(variant === 'primary' && {
			'backgroundColor': 'var(--color-brand-primary)',
			'color': '#fff',
			'&:hover': {
				backgroundColor: 'var(--color-brand-primary-dark)',
				transform: 'translateY(-1px)',
				boxShadow: '0 4px 12px rgba(30, 86, 49, 0.3)',
			},
		}),

		...(variant === 'ghost' && {
			'border': '1px solid var(--color-border-strong)',
			'color': 'var(--color-text-primary)',
			'backgroundColor': 'transparent',
			'&:hover': {
				borderColor: 'var(--color-brand-primary)',
				color: 'var(--color-brand-primary)',
			},
		}),

		...(variant === 'danger' && {
			'border': '1px solid var(--color-error)',
			'color': 'var(--color-error)',
			'backgroundColor': 'transparent',
			'&:hover': {
				backgroundColor: 'rgba(239, 68, 68, 0.06)',
			},
		}),

		'&:disabled': {
			opacity: 0.6,
			cursor: 'not-allowed',
			transform: 'none !important',
		},

		'minHeight': '44px',

		[down('sm')]: {
			minHeight: '48px',
			padding: '14px 18px',
			fontSize: '14px',
		},
	})
);

// Two-button row inside a verification slot's action area: a primary "capture"
// button + a ghost "upload from gallery" button. Stacks vertically on the
// smallest phones so each tap target stays comfortable; sits side-by-side
// from sm upward.
export const SlotActionRow = styled('div')({
	'display': 'flex',
	'flexDirection': 'row',
	'gap': '8px',
	'alignItems': 'stretch',
	'flexWrap': 'wrap',
	'& > button': {
		flex: '1 1 0',
		minWidth: 0,
	},
	[down('sm')]: {
		flexDirection: 'column',
	},
});

export const ResultChip = styled(domTag('div', ['tone']))(({ tone }: { tone: 'success' | 'warning' | 'danger' }) => ({
	display: 'inline-flex',
	alignItems: 'center',
	gap: '6px',
	padding: '5px 10px',
	borderRadius: '999px',
	fontSize: '11px',
	fontFamily: 'var(--font-family-semibold)',
	letterSpacing: 'var(--tracking-wide)',
	color:
		tone === 'success'
			? 'var(--color-success-text)'
			: tone === 'warning'
				? 'var(--color-warning-text)'
				: 'var(--color-error-text)',
	backgroundColor:
		tone === 'success' ? 'var(--color-success-bg)' : tone === 'warning' ? 'var(--color-warning-bg)' : 'var(--color-error-bg)',
	marginTop: '6px',
	width: 'fit-content',
}));

export const ReconcileCallout = styled('div')({
	'gridColumn': '1 / -1',
	'marginTop': '8px',
	'padding': '12px',
	'borderRadius': '10px',
	'backgroundColor': 'var(--color-warning-bg)',
	'color': 'var(--color-warning-text)',
	'fontSize': 'var(--text-body-sm-size)',
	'lineHeight': 1.45,

	'& strong': {
		fontFamily: 'var(--font-family-semibold)',
		fontWeight: 'normal',
	},

	'& .actions': {
		marginTop: '10px',
		display: 'flex',
		gap: '8px',
		flexWrap: 'wrap',
	},

	'& .actionBtn': {
		'all': 'unset',
		'cursor': 'pointer',
		'fontFamily': 'var(--font-family-semibold)',
		'fontSize': '12px',
		'padding': '6px 12px',
		'borderRadius': '8px',
		'border': '1px solid currentColor',
		'transition': 'background-color 150ms ease',
		'&:hover': {
			backgroundColor: 'rgba(245, 158, 11, 0.1)',
		},
	},

	'& .actionBtn.primary': {
		backgroundColor: 'var(--color-warning-text)',
		color: '#fff',
		border: '1px solid var(--color-warning-text)',
	},
});

export const FooterNote = styled('p')({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: '12px',
	lineHeight: 1.5,
	color: 'var(--color-text-tertiary)',
	display: 'flex',
	alignItems: 'flex-start',
	gap: '6px',
});

export const SkipLink = styled('button')({
	'all': 'unset',
	'cursor': 'pointer',
	'fontFamily': 'var(--font-family-medium)',
	'fontSize': '13px',
	'color': 'var(--color-text-secondary)',
	'textDecoration': 'underline',
	'textUnderlineOffset': '3px',
	'marginTop': '4px',
	'minHeight': '36px',
	'padding': '8px 0',
	'display': 'inline-flex',
	'alignItems': 'center',
	'&:hover': {
		color: 'var(--color-text-primary)',
	},
});

// =====================================================================
// Desktop camera capture modal
//
// Mounted alongside the IdentityVerificationCard but only ever opens on a
// non-touch device (mobile users still get the OS-native camera via the
// `<input capture>` path). The modal layout is intentionally focused: a
// large live preview, a single primary capture button, and contextual
// fallbacks (multi-camera switcher, permission-denied / no-device states)
// — all sized so it works in a 400 px narrow window or on a 5K display.
// =====================================================================

export const CameraModalOverlay = styled('div')({
	position: 'fixed',
	inset: 0,
	zIndex: 1200,
	backgroundColor: 'rgba(15, 14, 28, 0.78)',
	backdropFilter: 'blur(8px)',
	WebkitBackdropFilter: 'blur(8px)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '24px',
	paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
	animation: 'verifyCheckPop 200ms ease-out',

	[down('sm')]: {
		padding: '0',
		paddingBottom: 'env(safe-area-inset-bottom)',
		alignItems: 'stretch',
	},
});

export const CameraModalCard = styled('div')({
	'width': '100%',
	'maxWidth': '560px',
	'maxHeight': 'min(92dvh, 760px)',
	'backgroundColor': 'var(--color-bg-elevated)',
	'borderRadius': '20px',
	'border': '1px solid var(--color-border-subtle)',
	'boxShadow': '0 24px 60px rgba(0, 0, 0, 0.45)',
	'overflow': 'hidden',
	'display': 'flex',
	'flexDirection': 'column',
	'position': 'relative',

	'&:before': {
		content: '""',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		height: '3px',
		background: 'linear-gradient(90deg, var(--color-brand-primary) 0%, var(--color-brand-accent) 100%)',
		zIndex: 2,
	},

	[down('sm')]: {
		maxWidth: '100%',
		maxHeight: '100dvh',
		height: '100dvh',
		borderRadius: 0,
		border: 'none',
	},
});

export const CameraModalHeader = styled('div')({
	'display': 'flex',
	'alignItems': 'flex-start',
	'justifyContent': 'space-between',
	'gap': '16px',
	'padding': '20px 22px 12px 22px',

	'& .titleblock': {
		display: 'flex',
		flexDirection: 'column',
		gap: '4px',
		minWidth: 0,
	},

	'& .eyebrow': {
		fontFamily: 'var(--font-family-medium)',
		fontSize: '10px',
		letterSpacing: 'var(--tracking-widest)',
		textTransform: 'uppercase',
		color: 'var(--color-brand-primary)',
		margin: 0,
	},

	'& .title': {
		fontFamily: 'var(--font-family-display)',
		fontSize: '22px',
		lineHeight: 1.2,
		color: 'var(--color-text-primary)',
		margin: 0,
	},

	'& .closeBtn': {
		'all': 'unset',
		'cursor': 'pointer',
		'width': '44px',
		'height': '44px',
		'borderRadius': '12px',
		'display': 'inline-flex',
		'alignItems': 'center',
		'justifyContent': 'center',
		'color': 'var(--color-text-secondary)',
		'border': '1px solid transparent',
		'transition': 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
		'&:hover': {
			backgroundColor: 'var(--color-bg-secondary)',
			borderColor: 'var(--color-border-default)',
			color: 'var(--color-text-primary)',
		},
		'&:focus-visible': {
			outline: '2px solid var(--color-brand-primary)',
			outlineOffset: '2px',
		},
	},
});

// Live-preview frame. Aspect ratio differs by mode: 1/1 for the selfie (matches
// the round thumb in the slot, gives a generous head-and-shoulders crop), and
// 1.586/1 for the voter card (ID-1 / CR80 aspect ratio so the viewfinder
// matches a real PVC).
// Mirror semantics are applied inline on the <video>/<img> by the consumer
// rather than as a styled prop so we don't forward an unknown boolean attribute
// to the DOM (MUI v4 styled doesn't filter unknown props).
export const CameraPreviewFrame = styled(domTag('div', ['mode']))(({ mode }: { mode: 'selfie' | 'card' }) => ({
	'position': 'relative',
	'width': '100%',
	'aspectRatio': mode === 'selfie' ? '1 / 1' : '1.586 / 1',
	'maxHeight': mode === 'selfie' ? '56dvh' : '46dvh',
	'backgroundColor': '#0c0c14',
	'overflow': 'hidden',
	'display': 'flex',
	'alignItems': 'center',
	'justifyContent': 'center',

	'& video, & img': {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		display: 'block',
	},

	'& .placeholderState': {
		position: 'absolute',
		inset: 0,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '12px',
		color: 'rgba(255, 255, 255, 0.7)',
		padding: '24px',
		textAlign: 'center',
	},
}));

// Decorative guide drawn on top of the live preview to help the user frame the
// shot. Uses SVG strokes + box-shadow for a soft vignette so the guide reads
// over both dark and bright preview backgrounds without obscuring the subject.
export const CameraOverlayGuide = styled(domTag('div', ['mode']))(({ mode }: { mode: 'selfie' | 'card' }) => ({
	position: 'absolute',
	inset: 0,
	pointerEvents: 'none',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',

	...(mode === 'selfie' && {
		'& .oval': {
			width: '64%',
			height: '86%',
			border: '2px dashed rgba(255, 255, 255, 0.72)',
			borderRadius: '50%',
			boxShadow: '0 0 0 2000px rgba(0, 0, 0, 0.34)',
		},
	}),

	...(mode === 'card' && {
		'& .cardFrame': {
			position: 'relative',
			width: '78%',
			height: '78%',
			borderRadius: '14px',
			boxShadow: '0 0 0 2000px rgba(0, 0, 0, 0.4)',
		},
		'& .corner': {
			position: 'absolute',
			width: '28px',
			height: '28px',
			borderColor: 'rgba(255, 255, 255, 0.9)',
			borderStyle: 'solid',
			borderWidth: 0,
		},
		'& .corner.tl': { top: 0, left: 0, borderTopWidth: '3px', borderLeftWidth: '3px', borderTopLeftRadius: '12px' },
		'& .corner.tr': { top: 0, right: 0, borderTopWidth: '3px', borderRightWidth: '3px', borderTopRightRadius: '12px' },
		'& .corner.bl': { bottom: 0, left: 0, borderBottomWidth: '3px', borderLeftWidth: '3px', borderBottomLeftRadius: '12px' },
		'& .corner.br': { bottom: 0, right: 0, borderBottomWidth: '3px', borderRightWidth: '3px', borderBottomRightRadius: '12px' },
	}),
}));

export const CameraStatusRow = styled('div')({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '8px',
	padding: '12px 22px',
	fontFamily: 'var(--font-family-regular)',
	fontSize: '13px',
	color: 'var(--color-text-secondary)',
	textAlign: 'center',
	minHeight: '44px',
});

// Footer that holds the primary capture button + ghost upload/switch links.
// Sized generously so the primary action is unmissable on any viewport.
export const CameraModalFooter = styled('div')({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
	padding: '14px 22px 22px 22px',
	backgroundColor: 'var(--color-bg-elevated)',
	borderTop: '1px solid var(--color-border-subtle)',
});

export const CameraCaptureButton = styled('button')({
	'all': 'unset',
	'cursor': 'pointer',
	'display': 'inline-flex',
	'alignItems': 'center',
	'justifyContent': 'center',
	'gap': '10px',
	'width': '100%',
	'minHeight': '56px',
	'padding': '0 22px',
	'borderRadius': '999px',
	'background': 'linear-gradient(180deg, var(--color-brand-primary) 0%, var(--color-brand-primary-dark) 100%)',
	'color': '#fff',
	'fontFamily': 'var(--font-family-semibold)',
	'fontSize': '15px',
	'letterSpacing': 'var(--tracking-wide)',
	'boxShadow': '0 8px 22px rgba(30, 86, 49, 0.32)',
	'transition': 'transform 150ms ease, box-shadow 150ms ease, opacity 150ms ease',

	'&:hover': {
		transform: 'translateY(-1px)',
		boxShadow: '0 10px 26px rgba(30, 86, 49, 0.42)',
	},
	'&:active': {
		transform: 'translateY(0)',
		boxShadow: '0 6px 16px rgba(30, 86, 49, 0.32)',
	},
	'&:focus-visible': {
		outline: '2px solid var(--color-brand-accent)',
		outlineOffset: '3px',
	},
	'&:disabled': {
		opacity: 0.6,
		cursor: 'not-allowed',
		transform: 'none !important',
		boxShadow: 'none',
	},
});

// Secondary actions row: "Retake" + "Use this photo", or "Try again" + "Upload
// instead", etc. Stacks vertically only when there isn't space.
export const CameraActionRow = styled('div')({
	'display': 'flex',
	'gap': '10px',
	'& > *': {
		flex: '1 1 0',
		minWidth: 0,
	},
	[down('sm')]: {
		flexDirection: 'column',
	},
});

// Compact ghost / outline buttons used inside the camera modal — separate from
// the slot-level SlotActionButton so we can tune the visual weight against a
// dark/blurred backdrop.
export const CameraGhostButton = styled(domTag('button', ['variant']))(
	({ variant = 'ghost' }: { variant?: 'ghost' | 'primary' }) => ({
		'all': 'unset',
		'cursor': 'pointer',
		'display': 'inline-flex',
		'alignItems': 'center',
		'justifyContent': 'center',
		'gap': '8px',
		'minHeight': '48px',
		'padding': '0 18px',
		'borderRadius': '12px',
		'fontFamily': 'var(--font-family-semibold)',
		'fontSize': '14px',
		'transition': 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',

		...(variant === 'ghost' && {
			'border': '1px solid var(--color-border-strong)',
			'color': 'var(--color-text-primary)',
			'backgroundColor': 'transparent',
			'&:hover': {
				borderColor: 'var(--color-brand-primary)',
				color: 'var(--color-brand-primary)',
			},
		}),

		...(variant === 'primary' && {
			'border': '1px solid var(--color-brand-primary)',
			'color': '#fff',
			'backgroundColor': 'var(--color-brand-primary)',
			'&:hover': {
				backgroundColor: 'var(--color-brand-primary-dark)',
			},
		}),

		'&:focus-visible': {
			outline: '2px solid var(--color-brand-primary)',
			outlineOffset: '2px',
		},
	})
);

// Small camera-switcher pill — appears top-left over the preview when more
// than one camera is detected. Discreet so it doesn't fight the capture CTA.
export const CameraSwitchPill = styled('button')({
	'all': 'unset',
	'cursor': 'pointer',
	'position': 'absolute',
	'top': '12px',
	'left': '12px',
	'display': 'inline-flex',
	'alignItems': 'center',
	'gap': '6px',
	'padding': '8px 12px',
	'minHeight': '36px',
	'borderRadius': '999px',
	'backgroundColor': 'rgba(15, 14, 28, 0.72)',
	'border': '1px solid rgba(255, 255, 255, 0.22)',
	'color': '#fff',
	'fontFamily': 'var(--font-family-semibold)',
	'fontSize': '12px',
	'letterSpacing': 'var(--tracking-wide)',
	'backdropFilter': 'blur(6px)',
	'WebkitBackdropFilter': 'blur(6px)',
	'transition': 'background-color 150ms ease',

	'&:hover': {
		backgroundColor: 'rgba(15, 14, 28, 0.88)',
	},
	'&:focus-visible': {
		outline: '2px solid var(--color-brand-accent)',
		outlineOffset: '2px',
	},
});

// Error/permission-denied state shown in place of the live preview.
export const CameraErrorState = styled('div')({
	'display': 'flex',
	'flexDirection': 'column',
	'alignItems': 'center',
	'justifyContent': 'center',
	'gap': '12px',
	'padding': '40px 24px',
	'textAlign': 'center',
	'color': 'var(--color-text-primary)',

	'& .errIcon': {
		width: '56px',
		height: '56px',
		borderRadius: '50%',
		backgroundColor: 'var(--color-warning-bg)',
		color: 'var(--color-warning-text)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: '26px',
	},

	'& .errTitle': {
		fontFamily: 'var(--font-family-display)',
		fontSize: '20px',
		lineHeight: 1.2,
		margin: 0,
	},

	'& .errBody': {
		fontFamily: 'var(--font-family-regular)',
		fontSize: '14px',
		lineHeight: 1.5,
		color: 'var(--color-text-secondary)',
		margin: 0,
		maxWidth: '34ch',
	},
});
